import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import {
  Camera,
  CheckCircle,
  ImageSquare,
  SpinnerGap,
  WarningCircle,
} from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { normalizeEventNameList } from '../../config/eventTypes';

const API_URL = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '')}/api`;

function getSessionId() {
  const key = 'galago_photo_wall_session';
  let value = window.localStorage.getItem(key);
  if (!value) {
    value = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    window.localStorage.setItem(key, value);
  }
  return value;
}

const reasonLabels = {
  invalid_image: 'That file could not be read as a photo.',
  image_too_blurry: 'The photo is too blurry.',
  duplicate_photo: 'This photo has already been shared.',
  moderation_pending: 'The safety check is temporarily delayed. Your photo will appear automatically after it passes.',
};

export default function PhotoWallCapturePage() {
  const { token } = useParams();
  const [wall, setWall] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const cameraInputRef = useRef(null);
  const libraryInputRef = useRef(null);

  useEffect(() => {
    axios.get(`${API_URL}/photo-wall/${token}`)
      .then((response) => setWall(response.data))
      .catch((requestError) => setError(requestError.response?.data?.detail || 'This photo wall link is unavailable.'))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => () => {
    if (previewUrl) window.URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  const openFileInput = (inputRef) => {
    setError('');
    setResult(null);
    if (inputRef.current) {
      inputRef.current.value = '';
      inputRef.current.click();
    }
  };

  const selectFile = (file) => {
    if (!file) return;
    if (previewUrl) window.URL.revokeObjectURL(previewUrl);
    setSelectedFile(file);
    setPreviewUrl(window.URL.createObjectURL(file));
    setResult(null);
  };

  const handleFileSelection = (event) => {
    const file = event.target.files?.[0];
    if (file) selectFile(file);
    event.target.value = '';
  };

  const resetPreview = () => {
    if (previewUrl) window.URL.revokeObjectURL(previewUrl);
    setPreviewUrl('');
    setSelectedFile(null);
    setResult(null);
  };

  const uploadPhoto = async () => {
    if (!selectedFile) return;
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('anonymous_session_id', getSessionId());
    formData.append('captured_at', new Date().toISOString());
    try {
      setUploading(true);
      setError('');
      const response = await axios.post(`${API_URL}/photo-wall/${token}/upload`, formData);
      setResult(response.data);
      if (response.data.approved || response.data.accepted) {
        setSelectedFile(null);
        if (previewUrl) window.URL.revokeObjectURL(previewUrl);
        setPreviewUrl('');
      }
    } catch (uploadError) {
      setError(uploadError.response?.data?.detail || 'The photo could not be uploaded. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const names = (
    wall?.event_type === 'wedding'
      ? [wall?.bride_name, wall?.groom_name]
      : wall?.event_type === 'birthday'
        ? normalizeEventNameList(wall?.honoree_names)
        : (wall?.host_names || [])
  ).filter(Boolean).join(' & ') || wall?.event_name;

  if (loading) {
    return <div className="flex min-h-screen items-center justify-center bg-[#201a1d] text-white"><SpinnerGap className="h-9 w-9 animate-spin" /></div>;
  }

  return (
    <main className="min-h-screen bg-[radial-gradient(circle_at_top,#745964,#211a1d_58%)] px-4 py-8 text-white">
      <div className="mx-auto max-w-lg">
        <header className="text-center">
          <p className="text-xs uppercase tracking-[0.36em] text-white/60">GalaGo Live Photo Wall</p>
          <h1 className="mt-4 font-serif text-3xl">{names || 'Share a celebration moment'}</h1>
          <p className="mt-2 text-sm text-white/65">{wall?.source?.label}</p>
        </header>

        <section className="mt-8 overflow-hidden rounded-[2rem] border border-white/15 bg-white/10 p-4 shadow-2xl backdrop-blur">
          {!wall?.enabled || !wall?.upload_enabled ? (
            <div className="py-16 text-center">
              <WarningCircle className="mx-auto h-12 w-12 text-amber-300" />
              <p className="mt-4 text-lg">Guest uploads are currently closed.</p>
            </div>
          ) : previewUrl ? (
            <div>
              <img src={previewUrl} alt="Photo preview" className="aspect-[3/4] w-full rounded-2xl object-cover" />
              <div className="mt-4 grid grid-cols-2 gap-3">
                <Button type="button" variant="secondary" className="rounded-full" onClick={resetPreview} disabled={uploading}>Retake</Button>
                <Button type="button" className="rounded-full bg-white text-[#493842] hover:bg-white/90" onClick={uploadPhoto} disabled={uploading}>
                  {uploading ? <SpinnerGap className="mr-2 h-5 w-5 animate-spin" /> : <CheckCircle className="mr-2 h-5 w-5" />}
                  {uploading ? 'Checking...' : 'Share photo'}
                </Button>
              </div>
            </div>
          ) : (
            <div className="py-8">
              <div className="flex aspect-[4/3] flex-col items-center justify-center rounded-2xl border border-dashed border-white/25 bg-black/15">
                <Camera className="h-14 w-14 text-white/65" />
                <p className="mt-4 text-center text-sm text-white/70">Take a photo—no app or registration needed.</p>
              </div>
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                capture="environment"
                className="sr-only"
                onChange={handleFileSelection}
                data-testid="photo-wall-camera-input"
              />
              <input
                ref={libraryInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="sr-only"
                onChange={handleFileSelection}
                data-testid="photo-wall-library-input"
              />
              <Button type="button" onClick={() => openFileInput(cameraInputRef)} className="mt-4 h-14 w-full rounded-full bg-white text-[#493842] hover:bg-white/90">
                <Camera className="mr-2 h-6 w-6" /> Open camera
              </Button>
              <Button type="button" variant="ghost" className="mt-2 w-full rounded-full text-white hover:bg-white/10 hover:text-white" onClick={() => openFileInput(libraryInputRef)}>
                <ImageSquare className="mr-2 h-5 w-5" /> Choose from phone
              </Button>
            </div>
          )}

          {result && (
            <div className={`mt-4 rounded-2xl p-4 text-sm ${result.approved || result.accepted ? 'bg-emerald-400/20 text-emerald-50' : 'bg-amber-400/20 text-amber-50'}`}>
              {result.approved || result.accepted ? (
                <><CheckCircle className="mr-2 inline h-5 w-5" /> Thanks! Your photo has been received.</>
              ) : (
                <>
                  <WarningCircle className="mr-2 inline h-5 w-5" />
                  {result.reasons?.map((reason) => reasonLabels[reason] || 'The automatic moderator could not publish this photo.').join(' ')}
                </>
              )}
            </div>
          )}
          {error && <div className="mt-4 rounded-2xl bg-red-400/20 p-4 text-sm text-red-50">{error}</div>}
        </section>
        <p className="mt-5 text-center text-xs leading-5 text-white/45">Photos are checked automatically for blur, duplicates, and unsafe content before appearing live.</p>
      </div>
    </main>
  );
}
